/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';
import { mergeClasses, cn, addClasses } from '#root/lib/helpers/class.js';
import { useGallery } from '#@itinerary/gallery/use-gallery.js';
import { GalleryEditor } from '#@itinerary/gallery/gallery-editor.jsx';
import ImagePreview from '#components/images/image-preview.jsx';

export function MainTemplate(props) {
    const { description, name, price, gallery, roomGallery, dayId, planId, roomId, currentData } = props;

    const initialGallery = roomGallery ?? gallery;
    const galleryProps = useGallery({
        initialData: initialGallery
    });

    return (
        <div className="flex-col space-y-2">
            {initialGallery && initialGallery.length > 0 && (
                <GalleryEditor {...galleryProps} dayId={dayId} planId={planId} roomId={roomId} template={true} />
            )}
            <div className="w-full flex gap-2">
                <TemplateDiffData
                    className={addClasses('w-full')}
                    label={_t.edit.fields.title()}
                    oldValue={currentData?.name}
                    value={name}
                />
                {price ? (
                    <TemplateDiffData
                        className={addClasses('w-1/3')}
                        label={_t.edit.fields.price(false)}
                        oldValue={currentData?.price}
                        value={price}
                    />
                ) : null}
            </div>
            {description && (
                <TemplateDiffData
                    label={_t.edit.fields.description()}
                    oldValue={currentData?.description}
                    value={description}
                />
            )}
        </div>
    );
}

export function TransferTemplate(props) {
    const {
        currentData,
        transportation,
        vehicleNumber,
        departureTime,
        arrivalTime,
        arrival,
        departure,
        phoneNumber,
        company,
        pax
    } = props;

    return (
        <>
            <TemplateDiffData label={_t.edit.fields.departure()} value={departure} />
            <TemplateDiffData label={_t.edit.fields.arrival()} value={arrival} />

            <div className="flex gap-4">
                <div className="w-1/2">
                    <TemplateDiffData
                        label={_t.edit.fields.departureTime()}
                        oldValue={currentData?.departureTime}
                        value={departureTime}
                    />
                </div>
                <div className="w-1/2">
                    <TemplateDiffData
                        label={_t.edit.fields.arrivalTime()}
                        oldValue={currentData?.arrivalTime}
                        value={arrivalTime}
                    />
                </div>
            </div>

            <TemplateDiffData
                label={_t.edit.fields.transferType()}
                oldValue={currentData?.transportation}
                value={transportation}
            />

            {transportation && (
                <TemplateDiffData
                    label={_t.edit.fields.vehicleNumber(transportation && transportation !== 'O' ? transportation : '')}
                    oldValue={currentData?.vehicleNumber}
                    value={vehicleNumber}
                />
            )}

            {transportation === 'O' && (
                <TemplateDiffData
                    label={_t.edit.fields.phoneNumber()}
                    oldValue={currentData?.phoneNumber}
                    value={phoneNumber}
                    className="w-1/2"
                />
            )}

            <TemplateDiffData label={_t.edit.fields.company()} oldValue={currentData?.company} value={company} />
            <TemplateDiffData label={_t.edit.fields.passengers()} oldValue={currentData?.pax} value={pax} />
        </>
    );
}

export function SuggestionTemplate(props) {
    const { currentData, name, suggestionType, previewMode, defaultText } = props;

    return (
        <>
            <TemplateDiffData label={_t.edit.fields.name()} oldValue={currentData?.suggestion?.name} value={name} />
            <TemplateDiffData
                label={_t.edit.fields.previewMode()}
                oldValue={currentData?.previewMode}
                value={previewMode}
            />
            <TemplateDiffData
                label={_t.edit.fields.category()}
                oldValue={currentData?.suggestionType}
                value={suggestionType}
            />
            <TemplateDiffData
                label={_t.edit.suggestionDefaultText(previewMode)}
                oldValue={currentData?.defaultText}
                value={defaultText}
            />
        </>
    );
}

export function ActivityTemplate(props) {
    const { currentData, city, meetingPoint, startTime, duration } = props;

    return (
        <>
            <TemplateDiffData
                label={_t.edit.fields.meetingPoint()}
                oldValue={currentData?.meetingPoint}
                value={meetingPoint}
            />
            <TemplateDiffData label={_t.edit.fields.city()} oldValue={currentData?.city} value={city} />
            <div className="flex gap-4">
                <div className="w-1/2">
                    <TemplateDiffData
                        label={_t.edit.fields.startTime()}
                        oldValue={currentData?.startTime}
                        value={startTime}
                    />
                </div>
                <div className="w-1/2">
                    <TemplateDiffData
                        label={_t.edit.fields.duration()}
                        oldValue={currentData?.duration}
                        value={duration || '0'}
                    />
                </div>
            </div>
        </>
    );
}

export function AccommodationTemplate(props) {
    const { currentData, category, stars, address, phoneNumber, city, checkInTime, nights } = props;

    return (
        <>
            {address && (
                <div className="w-full">
                    <TemplateDiffData
                        label={_t.edit.fields.address()}
                        oldValue={currentData?.address}
                        value={address}
                        name="address"
                    />
                </div>
            )}
            <div className="w-full flex gap-x-4">
                <TemplateDiffData
                    label={_t.edit.fields.city()}
                    oldValue={currentData?.city}
                    value={city}
                    className={addClasses('w-full')}
                />
                <TemplateDiffData
                    label={_t.edit.fields.phoneNumber()}
                    oldValue={currentData?.phoneNumber}
                    value={phoneNumber}
                    className={addClasses('w-1/2')}
                />
            </div>
            <div className="w-full flex gap-x-4">
                <TemplateDiffData
                    label={_t.edit.fields.checkInTime()}
                    oldValue={currentData?.checkInTime}
                    value={checkInTime || '1'}
                    className={addClasses('w-[50%]')}
                    style={{ paddingRight: 0 }}
                />

                <TemplateDiffData
                    label={_t.edit.fields.nights()}
                    oldValue={currentData?.nights}
                    value={nights || '1'}
                    className={addClasses('w-[50%]')}
                />
            </div>
            <div className="w-full flex gap-x-4">
                <TemplateDiffData
                    label={_t.edit.fields.category()}
                    oldValue={currentData?.category}
                    value={category || ''}
                    className={addClasses('w-full min-w-1/2')}
                    style={{ paddingRight: 0 }}
                />
                <TemplateDiffData
                    label={_t.edit.fields.stars()}
                    oldValue={currentData?.stars}
                    value={stars || '0'}
                    className={addClasses('w-full min-w-1/2')}
                />
            </div>
            {/* TODO: capire se va gestita la possibilità di invalid fields in rooms */}
            <Rooms {...props} />
        </>
    );
}

export function Rooms(props) {
    const { currentData, rooms } = props;
    if (!rooms || !rooms.length) return null;

    return (
        <>
            <h2 className="font-bold text-neutral-700 text-xl text-center mt-4 mb-2">{_t.edit.planner.rooms()}</h2>

            {rooms && rooms.length > 0 && (
                <div className="flex flex-col">
                    {rooms.map((room, index) => {
                        const currentRoom = currentData?.rooms?.find((r) => r.importId === room.importId);
                        return (
                            <RoomTemplate
                                key={index}
                                roomId={room.id}
                                roomGallery={room.gallery}
                                roomName={room.name}
                                roomDescription={room.description}
                                roomPax={room.pax}
                                roomPrice={room.price}
                                roomIndex={room?.index}
                                tempIndex={index + 1}
                                currentRoom={currentRoom}
                                {...props}
                            />
                        );
                    })}
                </div>
            )}
        </>
    );
}

export function RoomTemplate(props) {
    const {
        currentRoom,
        tempIndex,
        roomId,
        roomIndex,
        roomGallery,
        roomName,
        roomDescription,
        roomPax,
        roomPrice,
        onEdit,
        dayId,
        planId,
        name: planName
    } = props;

    return (
        <div className="room">
            <div className="py-2 pl-2 pr-1 cursor-pointer rounded flex justify-between hover:bg-neutral-100">
                <h3 className="font-bold text-neutral-700 text-md">{roomName || `Room ${tempIndex}`}</h3>
            </div>
            <div className="flex flex-col space-y-2 p-0 m-0 block my-2 pl-2 pr-1">
                <MainTemplate
                    currentData={currentRoom}
                    city={planName}
                    name={roomName || `Room ${tempIndex}`}
                    description={roomDescription}
                    pax={roomPax}
                    price={roomPrice}
                    onEdit={onEdit}
                    dayId={dayId}
                    planId={planId}
                    roomId={roomId ?? roomIndex}
                    roomGallery={roomGallery}
                />
                <TemplateDiffData
                    label={_t.edit.fields.pax()}
                    oldValue={currentRoom?.pax}
                    value={roomPax}
                    name="pax"
                    type="number"
                />
            </div>
        </div>
    );
}

export function PlanTemplate(props) {
    const { name, title, type, setUnsavedChanges, template, summaryMode, ...rest } = props;

    return (
        <div className="relative flex flex-col gap-4 px-4">
            {(title || type) && !summaryMode && (
                <TemplateHeading>
                    <span className="whitespace-nowrap text-ellipsis overflow-hidden">
                        {title || type?.[0]?.toUpperCase() + type?.slice(1)}
                    </span>
                </TemplateHeading>
            )}
            {type !== 'suggestion' ? (
                <MainTemplate {...props} setUnsavedChanges={setUnsavedChanges} />
            ) : (
                <SuggestionTemplate {...props} />
            )}

            {type === 'accommodation' && <AccommodationTemplate {...props} />}
            {type === 'activity' && <ActivityTemplate {...props} />}
            {type === 'transfer' && <TransferTemplate {...props} />}
        </div>
    );
}

export function PlaceTemplate(props) {
    const { type, title, description, name, gallery, currentData } = props;

    const galleryProps = useGallery({
        initialData: gallery
    });

    return (
        <div className="relative flex flex-col gap-4 px-4 pb-4 h-full">
            {(title || type) && (
                <TemplateHeading>
                    <span className="whitespace-nowrap text-ellipsis overflow-hidden">
                        {title || type?.[0]?.toUpperCase() + type?.slice(1)}
                    </span>
                </TemplateHeading>
            )}
            <div className="flex-col space-y-2">
                {gallery && gallery.length > 0 && <GalleryEditor {...galleryProps} template={true} />}
                <div className="w-full flex gap-2">
                    <TemplateDiffData
                        className={addClasses('w-full')}
                        label={_t.edit.fields.title()}
                        oldValue={currentData?.name}
                        value={name}
                    />
                </div>
                {description && (
                    <TemplateDiffData
                        label={_t.edit.fields.description()}
                        oldValue={currentData?.description}
                        value={description}
                    />
                )}
            </div>
        </div>
    );
}

export function TemplateHeading(props) {
    const { children, className } = props;
    return (
        <h3
            className={mergeClasses(
                'w-full min-h-[30px] pt-2 font-semibold text-neutral-700 flex justify-between items-center',
                className
            )}
        >
            {children}
        </h3>
    );
}

export function TemplateData(props) {
    const { label, value, className, labelClassName, valueClassName } = props;
    if (!value) return null;
    const dangerousValue = {
        __html: value
    };
    return (
        <div className={mergeClasses('w-full py-1 px-2 bg-neutral-50 border border-neutral-200 rounded', className)}>
            <p
                className={mergeClasses(
                    'w-full whitespace-nowrap text-sm text-neutral-800 font-semibold',
                    labelClassName
                )}
            >
                {label}
            </p>
            <p className={mergeClasses('w-full text-sm', valueClassName)} dangerouslySetInnerHTML={dangerousValue} />
        </div>
    );
}

export function TemplateDiffData(props) {
    const { labelKey, label, oldValue, value, className, labelClassName, valueClassName } = props;
    if (value === null || value === undefined) return null;
    if ((labelKey === 'cover' || labelKey === 'intro') && oldValue.src !== value.src)
        return (
            <>
                <p className={mergeClasses('w-full text-sm text-neutral-800 font-semibold', labelClassName)}>{label}</p>
                <ImagePreview imageSrc={value.src} initialState={value} alt={value.src} />
            </>
        );

    if (labelKey === 'jingle' && oldValue.src !== value.src)
        return (
            <>
                <p className={mergeClasses('w-full text-sm text-neutral-800 font-semibold', labelClassName)}>{label}</p>
                <div className="border border-neutral-200 rounded-lg">
                    <audio controls>
                        <source src={value.src} type="audio/mp3" />
                    </audio>
                </div>
            </>
        );
    if (labelKey === 'cover' || labelKey === 'intro' || labelKey === 'jingle') return null;

    if (oldValue === value || oldValue === null || oldValue === undefined)
        return (
            <TemplateData
                label={label}
                value={value}
                className={className}
                labelClassName={labelClassName}
                valueClassName={valueClassName}
            />
        );

    const oldDangerousValue = {
        __html: oldValue || _t.global.emptyString()
    };
    const dangerousValue = {
        __html: value || _t.global.emptyString()
    };

    return (
        <div
            className={mergeClasses(
                'w-full py-1 px-2 bg-white bg-primary-50 border border-primary-200 rounded',
                className
            )}
        >
            <p className={mergeClasses('w-full text-sm text-neutral-800 font-semibold', labelClassName)}>{label}</p>
            <p className="w-full text-sm underline">{_t.edit.updateItinerary.oldValue()}</p>
            <p
                className={mergeClasses('w-full text-sm mb-2', valueClassName)}
                dangerouslySetInnerHTML={oldDangerousValue}
            />
            <p className="w-full text-sm underline">{_t.edit.updateItinerary.newValue()}</p>
            <p className={mergeClasses('w-full text-sm', valueClassName)} dangerouslySetInnerHTML={dangerousValue} />
        </div>
    );
}
